<script lang="ts">

    import 'mapbox-gl/dist/mapbox-gl.css';
    import { AnimationOptions, CameraOptions, Map as Mapgl, MapTouchEvent } from 'mapbox-gl';
    import type { LngLatBoundsLike } from 'mapbox-gl';
    import { createEventDispatcher, onMount, setContext } from 'svelte';
    import { readable, writable } from 'svelte/store';
    import type { Coordinates, MapLookAtBounds } from '../global';
    import { MAPBOX_ACCESS_KEY, MAPBOX_STYLE } from '../environment';

    const dispatch = createEventDispatcher();
    const mapWritable = writable<Mapgl>(null);
    const mapReadable = readable<Mapgl>(null, set => mapWritable.subscribe(set));

    let mapContainer: HTMLElement;
    let mapIsReady = false;
    export let map: Mapgl | null = null;

    setContext('map', mapReadable);

    onMount(() => {

        map = new Mapgl({
            accessToken: MAPBOX_ACCESS_KEY,
            container: mapContainer,
            style: MAPBOX_STYLE,
            center: [-16.514362650910837, 28.298957338073354],
            zoom: 5,
            dragRotate: false
        });

        map.on('load', () => {
            mapIsReady = true;
            mapWritable.set(map);
        });

        map.on('dragstart', () => dispatch('dragstart'));
        map.on('movestart', () => dispatch('movestart'));
        map.on('moveend', () => dispatch('moveend'));
        map.on('touchstart', touchstartHandler);
        map.on('touchstart', touchstartHandler);
        map.on('touchmove', touchmoveHandler);
        map.on('touchend', touchendHandler);
        map.on('mousedown', () => dispatch('mousedown'));

        //Llamamos a resize un segundo despues de que el componente
        //haya montado para contemplar posibles cambios del layout
        setTimeout(() => map.resize(), 1000);

    });

    function touchstartHandler(event: MapTouchEvent) {
        dispatch('touchstart', event);
    }

    function touchmoveHandler(event: MapTouchEvent) {
        dispatch('touchmove', event);
    }

    function touchendHandler(event: MapTouchEvent) {
        dispatch('touchend', event);
    }

    export function setCenter(coords: Coordinates): void {
        const make = () => map.setCenter(coords);
        if (mapIsReady) {
            make();
        } else {
            map.once('load', make);
        }
    }

    export function panTo(coords: Coordinates, options?: AnimationOptions): Promise<void> {
        return new Promise(resolve => {
            const make = () => {
                map.once('moveend', () => resolve());
                map.panTo(coords, options);
            };
            if (mapIsReady) {
                make();
            } else {
                map.once('load', make);
            }
        });
    }

    export function jumpTo(options: CameraOptions): Promise<void> {
        return new Promise(resolve => {
            const make = () => {
                map.once('moveend', () => resolve());
                map.jumpTo(options);
            };
            if (mapIsReady) {
                make();
            } else {
                map.once('load', make);
            }
        });
    }

    export function flyTo(options: CameraOptions & AnimationOptions): Promise<void> {
        return new Promise(resolve => {
            const make = () => {
                map.once('moveend', () => resolve());
                map.flyTo(options);
            };
            if (mapIsReady) {
                make();
            } else {
                map.once('load', make);
            }
        });
    }

    export function fitBounds(bounds: LngLatBoundsLike): void {
        const make = () => {
            map.fitBounds(bounds, {
                padding: 50
            });
        };
        if (mapIsReady) {
            make();
        } else {
            map.once('load', make);
        }
    }

    export function mapLookAtBounds(lookAtBounds: MapLookAtBounds): Promise<void> {
        return new Promise(resolve => {
            const make = () => {
                const cameraOptions = map.cameraForBounds(lookAtBounds.bounds);
                map.once('moveend', () => resolve());
                map.flyTo({ ...cameraOptions, ...lookAtBounds.options});
            };
            if (mapIsReady) {
                make();
            } else {
                map.once('load', make);
            }
        });
    }

</script>

<style lang="scss">

    .map {
        background-color: #eee;
        height: 100%;
        overflow: hidden;
    }

    .map-container {
        height: 100%;
    }

</style>

<svelte:options accessors />

<div class="map">
    <div class="map-container" bind:this={mapContainer}></div>
    <slot></slot>
</div>
